// W36: thin wrapper over the generated channel-wa client (messages and
// phone numbers; the templates half of the same service lives in
// `./templates.ts`).
//
// Types come from `./generated/channel-wa` (regenerate with
// `pnpm --filter @repletoryreach/sdk-api generate channel-wa`); the request
// path stays on the house core so the envelope, cursor pagination,
// Idempotency-Key, token refresh and ApiError mapping behave exactly as
// before.
//
// W46 reconciled the two biggest divergences the cutover found: the
// send-message request is now the vocabulary `POST /v1/messages` actually
// binds (the old `phone_number_id/to_msisdn/msg_type/body` names were accepted
// by nothing and every send composed with them deserialised to an all-default
// record), and `listMessages()` targets a route the service now maps. See
// `_memory/2026-09-02-w46-contract-reconciliation-contacts-identity.md`.

import { http } from './_core/request.js';
import type { DashboardStat } from './_core/dashboard.js';
import type { Envelope, Meta } from './_core/envelope.js';
import type { Page, PageOpts } from './_core/pagination.js';
import { buildPageQuery } from './_core/pagination.js';
import type * as gen from './generated/channel-wa/index.js';

// ── Types (generated) ──────────────────────────────────────────────────────

// Platform-wide: `services/channel-wa`, like every service here, sets
// `JsonSerializerOptions.DefaultIgnoreCondition = WhenWritingNull`, so every
// nullable member below is ABSENT from the JSON rather than present as
// `null`. The generated types declare them `null | T` and required; read them
// defensively and never `in`-test them.

/**
 * Kept hand-written: `send_records.direction` is a free `string` column, so
 * the OpenAPI document cannot narrow it. These are the only two values the
 * service writes.
 */
export type MessageDirection = 'inbound' | 'outbound';

/**
 * ⚠ The `send_block` half is hand-written until the channel-wa OpenAPI
 * document is regenerated; the service emits it today and it is absent while
 * the number is sending.
 */
export type PhoneNumber = gen.PhoneNumberListItem & {
  /** Set only while this number cannot send at all. See {@link SendBlock}. */
  send_block?: SendBlock | null;
  /**
   * Who Meta bills for this number's messages (21 September 2026). Always
   * `platform` for a number connected today. `customer` marks a number that
   * joined on its owner's own Meta account before credit lines were shared:
   * its messages are recorded on the usage page and charged no credits.
   */
  meta_billing_payer?: MetaBillingPayer;
  /** WhatsApp calls (3 October 2026). The detail is {@link getCalling}. */
  calling_status?: CallingStatus;
  /** Also on the WhatsApp Business app (coexistence, 3 October 2026). */
  is_coexistence?: boolean;
  /** Coexistence chat history: `none`, `requested`, `importing`, `done`, `declined` or `failed`. */
  history_sync_status?: 'none' | 'requested' | 'importing' | 'done' | 'declined' | 'failed';
  /** Meta's 0 to 100 for the history it is sending. */
  history_progress?: number | null;
  /** When Meta disconnected the number, and Meta's reason code (such as `PRIMARY_INACTIVITY`). */
  disconnected_at?: string | null;
  disconnect_reason?: string | null;
  /**
   * Meta's review of the display name (5 October 2026), in Meta's words:
   * `APPROVED`, `AVAILABLE_WITHOUT_REVIEW`, `PENDING_REVIEW`, `DECLINED`,
   * `EXPIRED` or `NONE`. Absent when it has never been checked. See
   * {@link refreshNumberName}.
   */
  name_status?: NameStatus | null;
  /** Meta's reason code when it declined the name, such as `NAME_FORMAT_UNACCEPTABLE`. */
  name_rejection_reason?: string | null;
  /** When the status above was last read from Meta. */
  name_status_at?: string | null;
  /** A new name the business asked for on the shared account, waiting for the owner to make the change. */
  name_requested?: string | null;
  name_requested_at?: string | null;
};

/**
 * Who Meta bills for a number's messages. `platform`: the number is on the
 * platform's shared WhatsApp account and its messages are paid in credits.
 * `customer`: the business's own WhatsApp account (Growth and Scale, 5 October
 * 2026), where Meta bills the business directly and the platform records the
 * messages at zero.
 */
export type MetaBillingPayer = 'platform' | 'customer';

/** Meta's display-name review states. Kept open: Meta adds values without notice. */
export type NameStatus =
  | 'APPROVED'
  | 'AVAILABLE_WITHOUT_REVIEW'
  | 'PENDING_REVIEW'
  | 'DECLINED'
  | 'EXPIRED'
  | 'NONE'
  | (string & {});
export type AttachPhoneNumberRequest = gen.AttachPhoneNumberRequest;
export type PhoneNumberAttachResult = gen.PhoneNumberAttachResult;

/**
 * What the organisation's plan allows for one limit, and what it is using.
 *
 * Billing owns the subscription so it answers the allowance; the service that
 * owns the rows counts them. channel-wa carries the `numbers` one on
 * `GET /v1/numbers`, so a page can say "1 of 1 used" and disable Connect rather
 * than letting somebody walk through Meta's whole window to be refused at the
 * last step.
 *
 * `allowance` is absent when the plan puts no ceiling on this key: read
 * `unlimited`.
 */
export interface PlanAllowance {
  /** `numbers` on this service. */
  limit: string;
  allowance?: number | null;
  unlimited: boolean;
  /** How many the organisation is using right now. A number taken out of service does not hold a slot. */
  current: number;
  plan_code?: string | null;
  plan_name?: string | null;
}

/** `GET /v1/numbers` emits `meta: { count, allowance }` alongside `data`. */
export interface PhoneNumberListMeta extends Meta {
  /** Number of rows in `data`. The route is unpaged, so this is the whole set. */
  count?: number;
  /**
   * The plan's numbers allowance. Absent only when an older service is
   * answering; a console should treat that as "no ceiling known" rather than
   * as zero.
   */
  allowance?: PlanAllowance;
  /** WhatsApp's limit on new people a day, one entry per number (2 October 2026). */
  daily_limits?: NumberDailyLimit[];
  /**
   * Whether the plan includes the business's own WhatsApp account (Growth and
   * Scale, 5 October 2026). Absent from an older service.
   */
  own_account?: OwnAccountAllowance;
}

/** `meta.own_account` on `GET /v1/numbers`. */
export interface OwnAccountAllowance {
  allowed: boolean;
  plan_code?: string | null;
  plan_name?: string | null;
}

/**
 * Where a number stands against WhatsApp's limit on how many different people
 * it may start a conversation with, by template, in any 24 hours. The limit
 * rises with the number's Meta tier (`TIER_250`, `TIER_1K`, `TIER_10K`,
 * `TIER_100K`, `TIER_UNLIMITED`). A template to someone new past it is refused
 * with 429 `NUMBER_DAILY_LIMIT`; someone already messaged in the window can
 * still be reached.
 */
export interface NumberDailyLimit {
  number_id: string;
  tier: string;
  /** New people a day; absent or null when Meta sets no limit. */
  limit?: number | null;
  /** Different people sent a template in the last 24 hours. */
  used: number;
  remaining?: number | null;
  /** When the oldest of them stops counting. */
  resets_at?: string | null;
  /**
   * The number is on the platform's shared WhatsApp account (5 October 2026),
   * whose limit is divided fairly between the businesses on it. Then `limit`
   * is this business's share (or used plus remaining once spare room is
   * released) and `used` counts the business's own new people across all its
   * shared numbers.
   */
  shared?: boolean;
  /** This business's fair share of the shared account's limit. */
  share?: number | null;
  /** The shared account's whole limit, and how much of it every business together used. */
  account_limit?: number | null;
  account_used?: number | null;
  /** How many businesses share the account. */
  businesses?: number | null;
  /** After 18:00 SAST unused shares are released, so anyone may use the room left under the account's limit. */
  released?: boolean;
}

/**
 * The 429 body `POST /v1/messages` answers when a template would reach someone
 * new past the number's daily limit. On a shared-account number the limit is
 * the business's share and `shared` is true.
 */
export interface NumberDailyLimitError {
  code: 'NUMBER_DAILY_LIMIT';
  message: string;
  tier?: string;
  limit?: number | null;
  used?: number;
  retry_after_seconds?: number | null;
  shared?: boolean;
  share?: number | null;
  account_limit?: number | null;
  account_used?: number | null;
}

/** True when `err` is the daily-limit refusal, narrowing `err.body.error` to {@link NumberDailyLimitError}. */
export function isNumberDailyLimit(err: unknown): err is { status: 429; body: { error: NumberDailyLimitError } } {
  const e = err as { status?: number; body?: { error?: { code?: unknown } } } | null;
  return !!e && e.status === 429 && e.body?.error?.code === 'NUMBER_DAILY_LIMIT';
}

/**
 * Codes for a business's own WhatsApp account (5 October 2026).
 *
 * - `OWN_ACCOUNT_NOT_ON_PLAN`: 409 from `POST /v1/messages` when a number on
 *   the business's own account sends after a move to a plan without own
 *   accounts (Free, Lite, Plus).
 * - `NOT_ON_PLAN`: the same rule before connecting, on preflight, start and a
 *   409 from complete.
 * - `ACCEPT_META_BILLING`: start was called for a business-paid connection
 *   without `accept_meta_billing: true`.
 * - `OWN_ACCOUNT_NAME`: 409 from the name request on an own-account number,
 *   whose name changes in the business's own WhatsApp Manager.
 * - `NAME_INVALID`: 400, the asked-for name is not 3 to 100 characters.
 */
export const OWN_ACCOUNT_NOT_ON_PLAN = 'OWN_ACCOUNT_NOT_ON_PLAN';
export const NOT_ON_PLAN = 'NOT_ON_PLAN';
export const ACCEPT_META_BILLING = 'ACCEPT_META_BILLING';
export const OWN_ACCOUNT_NAME = 'OWN_ACCOUNT_NAME';
export const NAME_INVALID = 'NAME_INVALID';

/** The 409 body for {@link OWN_ACCOUNT_NOT_ON_PLAN} and for `NOT_ON_PLAN` from complete. */
export interface OwnAccountNotOnPlan {
  code: typeof OWN_ACCOUNT_NOT_ON_PLAN | typeof NOT_ON_PLAN;
  message: string;
  plan_code?: string | null;
  plan_name?: string | null;
  step?: string;
  session_id?: string;
}

/** True when `err` says the plan does not include an own WhatsApp account, on send or on connect. */
export function isOwnAccountNotOnPlan(err: unknown): err is { status: 409; body: { error: OwnAccountNotOnPlan } } {
  const e = err as { status?: number; body?: { error?: { code?: unknown } } } | null;
  const code = e?.body?.error?.code;
  return !!e && e.status === 409 && (code === OWN_ACCOUNT_NOT_ON_PLAN || code === NOT_ON_PLAN);
}

/** Where a business with its own WhatsApp account changes its display name. */
export const META_WHATSAPP_MANAGER_NUMBERS_URL = 'https://business.facebook.com/wa/manage/phone-numbers/';

/** What both display-name routes answer. */
export interface NumberName {
  id: string;
  display_name?: string | null;
  name_status?: NameStatus | null;
  name_rejection_reason?: string | null;
  name_status_at?: string | null;
  name_requested?: string | null;
  name_requested_at?: string | null;
  /** True when Meta answered just now; false when Meta could not be asked and the stored answer is returned. */
  checked_now: boolean;
}

/**
 * Ask Meta for the number's display-name review now, rather than waiting for
 * the webhook. 404 `PHONE_NOT_FOUND`.
 */
export function refreshNumberName(id: string): Promise<Envelope<NumberName>> {
  return http.post<Envelope<NumberName>>(`/v1/numbers/${encodeURIComponent(id)}/name/refresh`, { body: {} });
}

/**
 * Ask for a new display name on a number on the platform's shared account.
 * The owner makes the change in WhatsApp Manager and Meta reviews it; the
 * decision later updates `name_status`. 400 `NAME_INVALID` (3 to 100
 * characters), 404 `PHONE_NOT_FOUND`, 409 `OWN_ACCOUNT_NAME` for a number on
 * the business's own account.
 */
export function requestNumberName(id: string, name: string): Promise<Envelope<NumberName>> {
  return http.post<Envelope<NumberName>>(`/v1/numbers/${encodeURIComponent(id)}/name/request`, { body: { name } });
}

/**
 * The HTTP 409 body `POST /v1/numbers/attach` and
 * `POST /v1/numbers/embedded-signup/complete` both return when the plan's
 * numbers allowance is reached, so a console renders "You are on Starter,
 * which includes 1 number. Upgrade to add more." rather than a status code.
 *
 * Both paths refuse identically on purpose: a customer connecting their second
 * number through Meta's window must be told what one using the manual form is.
 */
export interface PlanLimitReached {
  code: 'PLAN_LIMIT_REACHED';
  message: string;
  limit: string;
  allowance?: number | null;
  current: number;
  plan_code?: string | null;
  plan_name?: string | null;
}

/** True when `err` is a plan-allowance refusal, narrowing `err.body.error` to {@link PlanLimitReached}. */
export { isPhoneNumberInvalid, type PhoneNumberInvalid } from './_core/phone.js';

export function isPlanLimitReached(err: unknown): err is { status: 409; body: { error: PlanLimitReached } } {
  const e = err as { status?: number; body?: { error?: { code?: unknown } } } | null;
  return !!e && e.status === 409 && e.body?.error?.code === 'PLAN_LIMIT_REACHED';
}

export interface PhoneNumberListEnvelope extends Envelope<PhoneNumber[]> {
  meta?: PhoneNumberListMeta;
}

/**
 * A message row as returned by `GET /v1/messages/{id}` - `gen.MessageDetail`
 * plus the six fields the hand-written client used to claim, kept (as
 * optional, because the projection never sends them) so the exported shape
 * does not shrink.
 */
export type MessageRecord = Omit<gen.MessageDetail, 'direction'> & {
  /**
   * ⚠ DRIFT: the service declares this `string`; narrowed to the two values
   * `services/channel-wa` actually writes.
   */
  direction: MessageDirection;
  /** ⚠ DRIFT: not in the `GET /v1/messages/{id}` projection: always absent. */
  phone_number_id?: string;
  /** ⚠ DRIFT: not in the projection: always absent. Derive send state from
   *  `sent_at` / `delivered_at` / `failed_at` instead. */
  status_code?: string;
  /** ⚠ DRIFT: not in the projection: always absent. The outbound payload is
   *  not read back. */
  body?: Record<string, unknown> | null;
  /** ⚠ DRIFT: not in the projection: always absent. The service tracks no
   *  read receipt on this route. */
  read_at?: string | null;
  /** ⚠ DRIFT: not in the projection: always absent. Failure detail is
   *  written to the record's `error` column, which is not exposed. */
  error_code?: string | null;
  /** ⚠ DRIFT: not in the projection: always absent. */
  created_at?: string;
};

export type SendTextPayload = gen.SendTextPayload;
export type SendTemplatePayload = gen.SendTemplatePayload;
/**
 * Reply buttons (`kind: 'button'`, up to three) or a list (`kind: 'list'`, up
 * to ten rows behind `button_text`). Sent with `type: 'interactive'`.
 */
export type SendInteractivePayload = gen.SendInteractivePayload;
export type SendOption = gen.SendOption;
/**
 * A media message: `type` is `image`, `video`, `audio` or `document` and this
 * carries `link` (a URL WhatsApp can fetch anonymously, such as a signed media
 * URL) or `id` (a media id already uploaded to Meta).
 */
export type SendMediaPayload = gen.SendMediaPayload;
export type TemplateComponent = gen.TemplateComponent;
export type TemplateParameter = gen.TemplateParameter;

/** Client-only. The core lifts this onto the `Idempotency-Key` header. */
interface WithIdempotencyKey {
  idempotency_key?: string;
}

/** The request body `POST /v1/messages` binds. */
export type SendMessagePayload = gen.SendMessageRequest;

/**
 * A send. `type: 'text'` carries `text: { body }`; `type: 'template'` carries
 * `template: { name, language, … }`; `type: 'interactive'` carries
 * `interactive: { kind, body, options, … }`; `type: 'image' | 'video' |
 * 'audio' | 'document'` carries `media: { link | id, caption?, filename? }`.
 *
 * W46 removed the parallel `phone_number_id / to_msisdn / msg_type / body`
 * vocabulary this module used to accept: the service binds
 * `(number_id, to, type, text?, template?)` and never bound the other four, so
 * a body in the old shape deserialised to an all-default record: the send
 * could not have been working from any caller that used it.
 */
export type SendMessageRequest = SendMessagePayload & WithIdempotencyKey;

/** 202 Accepted body: a fresh send. */
export type MessageAccepted = gen.MessageAccepted;
/** 200 OK body: an `Idempotency-Key` replay of an earlier send. */
export type MessageIdempotentReplay = gen.MessageIdempotentReplay;

/**
 * ⚠ DRIFT: `sendMessage()` used to be typed `Envelope<MessageRecord>`; the
 * service returns neither. A fresh send answers **202** with
 * `{ id, meta_message_id, status }` (`status` is `"sent"`, or `"failed"` when
 * Meta gave us no message id), and an `Idempotency-Key` replay answers **200**
 * with `{ id, meta_message_id }` and no `status`. `status` is therefore
 * optional: absent means "this was a replay", not "unknown".
 */
export type SendMessageResult = MessageIdempotentReplay &
  Partial<Pick<MessageAccepted, 'status'>>;

// ── Operations ─────────────────────────────────────────────────────────────

/**
 * Fails with 402 `INSUFFICIENT_CREDITS` (`{ code, required, available }`) and
 * 404 `PHONE_NOT_FOUND` (`{ code, number_id }`).
 */
export function sendMessage(req: SendMessageRequest): Promise<Envelope<SendMessageResult>> {
  const headers: Record<string, string> = {};
  if (req.idempotency_key) headers['idempotency-key'] = req.idempotency_key;
  return http.post<Envelope<SendMessageResult>>('/v1/messages', { body: req, headers });
}

/**
 * One send record in full.
 *
 * Wider than the list row: `read_at` and `error` had been on the table since
 * the first migration and were never returned, so a caller could learn that a
 * message was delivered and never that it was read, nor why one failed.
 *
 * `rendered_text` is what a template send actually said. A template carries no
 * text on the wire, so it is composed from the template and the parameter
 * values at the moment of sending and stored; a template edited afterwards
 * cannot change it. `rendered_source` says how much to trust the wording:
 * `composed` was written at send time and is exact, `recovered` was composed
 * later from the template as it stands now and is probable, `unrecoverable`
 * means the template is no longer on file and only the parameter values are
 * known.
 *
 * For the same message inside a conversation, `inbox.getMessage` answers the
 * fuller question, including what it cost.
 */
export interface MessageFullRecord extends MessageRecord {
  sender_msisdn?: string | null;
  created_at?: string;
  read_at?: string | null;
  template_name?: string | null;
  template_language?: string | null;
  template_category?: string | null;
  rendered_text?: string | null;
  rendered_facts?: RenderedTemplateFacts | null;
  rendered_source?: 'composed' | 'recovered' | 'unrecoverable' | null;
  /** Meta's own words about a failure, plus the platform's translation of them. */
  error?: {
    code?: string | null;
    title?: string | null;
    refusal_code?: string | null;
    message?: string | null;
    next_step?: string | null;
    action?: string | null;
    fault?: string | null;
  } | null;
}

/**
 * The parts of a template message that are not words. Structurally identical
 * to `inbox.TemplateFacts`, and deliberately so: one shape composed once in
 * channel-wa, projected onto the inbox message, so a console that can draw one
 * can draw the other.
 */
export interface RenderedTemplateFacts {
  header?: { format: string; text?: string | null; link?: string | null; filename?: string | null } | null;
  buttons?: { type: string; text?: string | null; url?: string | null; phone_number?: string | null }[] | null;
  params?: { component: string; index: number; kind: string; value: string }[] | null;
}

export function getMessage(id: string): Promise<Envelope<MessageFullRecord>> {
  return http.get<Envelope<MessageFullRecord>>(`/v1/messages/${encodeURIComponent(id)}`);
}

export function listPhoneNumbers(): Promise<PhoneNumberListEnvelope> {
  return http.get<PhoneNumberListEnvelope>('/v1/numbers');
}

/**
 * Manually attach a Meta phone number. Idempotent on meta_phone_number_id.
 *
 * `msisdn` is taken the way a person would dial it (`0615360809` under a
 * South African organisation is stored as `+27615360809`); a number that
 * cannot be read fails with 400 `PHONE_NUMBER_INVALID`, narrowed by
 * {@link isPhoneNumberInvalid}, whose body says what was assumed and why.
 *
 * Fails with 409 `PLAN_LIMIT_REACHED` past the plan's numbers allowance; use
 * {@link isPlanLimitReached} to narrow it. Re-attaching a number that is
 * already live never trips the limit, because it adds nothing to the count.
 */
export function attachPhoneNumber(
  req: AttachPhoneNumberRequest
): Promise<Envelope<PhoneNumberAttachResult>> {
  return http.post<Envelope<PhoneNumberAttachResult>>('/v1/numbers/attach', { body: req });
}

// ── Message log (W46) ──────────────────────────────────────────────────────
// Hand-typed against the house `Page` until the generator is next run against
// the reconciled service. Same projection as `getMessage`.

export interface MessageListOpts extends PageOpts {
  direction?: MessageDirection;
  /** Restrict to sends from one attached phone number. */
  number_id?: string;
  /** Restrict to one recipient MSISDN (exact match, normalised form). */
  to?: string;
  offset?: number;
}

export function listMessages(opts?: MessageListOpts): Promise<Page<MessageRecord>> {
  const query: Record<string, string | number | undefined> = { ...buildPageQuery(opts) };
  if (opts?.direction !== undefined) query.direction = opts.direction;
  if (opts?.number_id !== undefined) query.number_id = opts.number_id;
  if (opts?.to !== undefined) query.to = opts.to;
  if (opts?.offset !== undefined) query.offset = opts.offset;
  return http.get<Page<MessageRecord>>('/v1/messages', { query });
}

/**
 * Delivery rate over the trailing window, as a percentage of SETTLED outbound
 * sends. `value` is null when nothing settled, which the console renders as
 * "no sends yet" rather than as a 0% rate.
 */
export type MessagesDashboardSummary = gen.MessagesDashboardSummary & DashboardStat & {
  /**
   * Present only while at least one number cannot send at all. The delivery
   * rate cannot say this on its own: 0% reads the same whether one recipient
   * blocked the business or the whole WhatsApp account has no card on it, and
   * only the second is an outage the customer has to go and end.
   */
  send_block?: SendBlockSummary | null;
};

/** Delivery rate for the trailing window, with the previous window to compare against. */
export function getMessageSummary(windowHours?: number): Promise<Envelope<MessagesDashboardSummary>> {
  return http.get<Envelope<MessagesDashboardSummary>>('/v1/messages/summary', {
    query: windowHours === undefined ? undefined : { window_hours: windowHours }
  });
}

/** Fields a tenant may change on an attached number. Omit one to leave it. */
export interface UpdatePhoneNumberRequest {
  display_name?: string;
  msisdn?: string;
  /** `false` takes the number out of service without detaching it. */
  is_active?: boolean;
}

/**
 * Renames a number, or takes it out of service.
 *
 * Attaching used to be the only verb: a typo'd display name could only be
 * corrected by re-attaching with the same Meta id, and because that path
 * force-sets `is_active = true`, a number that had left the account stayed
 * "Active" forever.
 */
export function updatePhoneNumber(
  id: string,
  req: UpdatePhoneNumberRequest
): Promise<Envelope<gen.PhoneNumberUpdateResult>> {
  return http.patch<Envelope<gen.PhoneNumberUpdateResult>>(
    `/v1/numbers/${encodeURIComponent(id)}`,
    { body: req }
  );
}

// ── Removing a number from the account ─────────────────────────────────────
//
// Hand-typed against the service's `.Produces<T>()` records until the
// generator is next run.

/**
 * What removal did.
 *
 * Both flags are always true and are on the wire deliberately, because both
 * answer a question somebody will otherwise have to guess at.
 *
 * `history_kept`: the row and every send record on it stay exactly where they
 * are. `send_records.phone_number_id` cascades on delete, so a real DELETE
 * would take the whole message history of that number with it, silently; the
 * ledger, the usage figures and the conversations all hang off those rows.
 * Removal stamps the row instead and takes the number off the account.
 *
 * `still_on_whatsapp`: nothing is changed at Meta. The number stays on the
 * customer's WhatsApp Business Account, because removing it in one place
 * should not reshape their Meta account behind their back.
 */
export interface PhoneNumberRemovalResult {
  id: string;
  msisdn?: string | null;
  display_name?: string | null;
  removed_at: string;
  history_kept: boolean;
  still_on_whatsapp: boolean;
}

/**
 * Take a number off this account, keeping its history.
 *
 * Refused with 409 `NUMBER_IN_SERVICE` while the number is still active: take
 * it out of service first with {@link updatePhoneNumber}. That ordering makes
 * an accidental removal a two-step action, and by the second step the customer
 * has already seen what stops working.
 *
 * Attaching the same number afterwards restores it, with its conversations and
 * billing history still attached, which is how somebody recovers from removing
 * the wrong one.
 */
export function removePhoneNumber(id: string): Promise<Envelope<PhoneNumberRemovalResult>> {
  return http.delete<Envelope<PhoneNumberRemovalResult>>(`/v1/numbers/${encodeURIComponent(id)}`);
}

// ── Meta status (3 October 2026) ────────────────────────────────────────────
//
// What Meta has registered and verified for the platform, asked live: business
// verification, the app, the token and its permissions, the webhook fields, the
// WhatsApp accounts and numbers, and what stands between the coexistence switch
// and the feature. Platform admins only.

export type MetaStatusState = 'ok' | 'attention' | 'unknown' | 'later';

export interface MetaStatusItem {
  code: string;
  label: string;
  /** `unknown`: Meta did not answer or cannot be asked (never a no); `later`: needed only once a feature is on. */
  state: MetaStatusState;
  /** What Meta itself said, such as `verified`. */
  value?: string | null;
  detail: string;
}

export interface MetaStatusSection {
  code: string;
  title: string;
  items: MetaStatusItem[];
}

export interface MetaStatus {
  checked_at: string;
  configured: boolean;
  /** Lines needing a person. */
  attention: number;
  sections: MetaStatusSection[];
  coexistence: { enabled: boolean; switch: boolean; ready: boolean; missing: string[] };
}

/** Ask Meta (cached a minute; `refresh` asks again at once). */
export function metaStatus(opts?: { refresh?: boolean }): Promise<Envelope<MetaStatus>> {
  return http.get<Envelope<MetaStatus>>(`/v1/numbers/meta-status${opts?.refresh ? '?refresh=true' : ''}`);
}

// ── WhatsApp calls (3 October 2026) ────────────────────────────────────────
//
// A customer presses the call button in the business's WhatsApp chat; the call
// is answered like a phone call to the business (the assistant, or a
// department first), charged per minute at its own rate, and lands in the
// customer's WhatsApp thread. Hand-typed against `CallingDto` until the
// generator is next run.

export type CallingStatus = 'off' | 'on' | 'failed';

export type CallingDay = 'MONDAY' | 'TUESDAY' | 'WEDNESDAY' | 'THURSDAY' | 'FRIDAY' | 'SATURDAY' | 'SUNDAY';

/** One period a day is open, 24-hour `HH:mm`. A day may have two (a lunch break). */
export interface CallingHoursSlot {
  day: CallingDay;
  open: string;
  close: string;
}

/**
 * When calls are taken. `enabled: false` is any time. Outside the hours WhatsApp
 * still shows the call button and tells the caller the business is closed.
 */
export interface CallingHours {
  enabled: boolean;
  /** An IANA time zone. Default `Africa/Johannesburg`. */
  timezone: string;
  slots: CallingHoursSlot[];
}

/**
 * A number's WhatsApp calls. `reasons` lists what stands in the way of
 * switching them on, as codes: channel-wa's own (`CALLING_TIER_TOO_LOW`,
 * `CALLING_ON_BUSINESS_APP`, `CALLING_NUMBER_INACTIVE`,
 * `CALLING_VOICE_UNREACHABLE`) and voice's (`VOICE_NOT_ACTIVATED`,
 * `VOICE_WHATSAPP_OFF`, `VOICE_NOT_PRICED` and the rest).
 */
export interface Calling {
  number_id: string;
  status: CallingStatus;
  /** Why the last change was refused, as a code; null when it was not. */
  error?: string | null;
  hours: CallingHours;
  ready: boolean;
  reasons: string[];
  /** What a minute of a WhatsApp call costs, in cents, VAT in; null when not priced. */
  per_minute_minor?: number | null;
  /** The voice service's id for the number while it takes calls: its answering is set on the Voice page. */
  voice_number_id?: string | null;
  updated_at?: string | null;
}

export interface SetCallingRequest {
  enabled: boolean;
  hours?: CallingHours;
}

/** A number's WhatsApp calls, and anything stopping them being switched on. */
export function getCalling(id: string): Promise<Envelope<Calling>> {
  return http.get<Envelope<Calling>>(`/v1/numbers/${encodeURIComponent(id)}/calling`);
}

/**
 * Switch WhatsApp calls on or off for a number, or change its hours. Owners and
 * admins only. Refused with 409 `CALLING_NOT_READY` (its `reasons` say why, see
 * {@link isCallingNotReady}), with Meta's own refusal (`isMetaRefusal`), or with
 * 503 when the call engine cannot be reached.
 */
export function setCalling(id: string, req: SetCallingRequest): Promise<Envelope<Calling>> {
  return http.put<Envelope<Calling>>(`/v1/numbers/${encodeURIComponent(id)}/calling`, { body: req });
}

export function isCallingNotReady(err: unknown): err is { status: 409; body: { error: { code: 'CALLING_NOT_READY'; message: string; reasons: string[] } } } {
  const e = err as { status?: number; body?: { error?: { code?: string } } } | null;
  return !!e && e.status === 409 && e.body?.error?.code === 'CALLING_NOT_READY';
}

// ── WhatsApp account readiness (platform console) ──────────────────────────

/** One number as Meta holds it right now, with the remedy already worded. */
export interface NumberReadiness {
  id: string;
  display?: string | null;
  /** `connected` | `limited` | `on_business_app` | `unverified` | `not_registered` */
  state: 'connected' | 'limited' | 'on_business_app' | 'unverified' | 'not_registered';
  /** The sentence to show. Names what somebody has to go and do, not the Meta field that is off. */
  detail: string;
  usable: boolean;
}

/**
 * One WhatsApp Business Account, asked of Meta rather than read out of this
 * platform's own tables.
 *
 * `system_user_assigned` and `app_subscribed` are three-state. True is yes,
 * false is a definite no, and `undefined` means Meta would not answer the
 * question. Rendering the third as a no is the exact mistake this endpoint
 * exists to correct: a token with no assignment reads an account perfectly
 * well and is refused on every write, so both the yes and the "we could not
 * tell" have to stay distinguishable.
 */
export interface WabaReadiness {
  waba_id: string;
  organisation_id: string;
  numbers_on_account: number;
  system_user_assigned?: boolean;
  assigned_detail: string;
  app_subscribed?: boolean;
  subscribed_detail: string;
  numbers_detail?: string | null;
  numbers: NumberReadiness[];
  checked_at: string;
}

export interface WabaReadinessOpts {
  org_id?: string;
  waba_id?: string;
  /** 1-25, default 10. Each account costs three Graph reads. */
  limit?: number;
}

/**
 * Readiness per WhatsApp Business Account. Platform admin only.
 *
 * Live Graph reads, so it is slower than a table scan and deliberately
 * bounded. Use it where an operator needs to see that an account works before
 * a customer discovers it does not.
 */
export function wabaReadiness(
  opts: WabaReadinessOpts = {}
): Promise<Envelope<WabaReadiness[]> & { meta?: { count?: number } }> {
  const query: Record<string, string | number | undefined> = {};
  if (opts.org_id !== undefined) query.org_id = opts.org_id;
  if (opts.waba_id !== undefined) query.waba_id = opts.waba_id;
  if (opts.limit !== undefined) query.limit = opts.limit;
  return http.get<Envelope<WabaReadiness[]> & { meta?: { count?: number } }>(
    '/v1/numbers/waba-readiness',
    { query }
  );
}

// ── Guided WhatsApp connection: Meta Embedded Signup (W22) ─────────────────
//
// The customer presses one button, completes Meta's own dialog, and their
// number is attached and sending. Three calls make that work, and the shape of
// them is deliberate: nothing here carries a secret, because the platform app
// secret is what turns Meta's authorisation code into a token and that
// exchange happens on the server.
//
// The browser's job:
//   1. `start()`, then load Facebook's JS SDK and call `FB.login()` with the
//      `config_id` and `graph_version` it hands back,
//   2. listen for `window.addEventListener('message', …)` from facebook.com
//      for `{ type: 'WA_EMBEDDED_SIGNUP' }`; the FINISH event carries
//      `data.data.phone_number_id` and `data.data.waba_id`,
//   3. `complete()` with those two ids and the `code` from `FB.login()`'s
//      callback, or `cancel()` when the customer closed the window.
//
// Hand-typed against the service's `.Produces<T>()` records until the
// generator is next run: `pnpm --filter @repletoryreach/sdk-api generate
// channel-wa` regenerates `./generated/channel-wa` from the live document.

/**
 * What the browser needs to launch Meta's dialog.
 *
 * `available` is false when the platform has not finished setting the Meta app
 * up; the console then keeps the manual attach form and says the guided
 * connection is not on yet, rather than opening a window that cannot work.
 * `reason` is `NOT_CONFIGURED`, `NO_CONFIG_ID` or `NO_CREDIT_LINE` (the platform
 * has no Meta credit line to share yet, so a customer account connected now
 * would be billed by Meta directly), and the other fields are null.
 */
export type EmbeddedSignupStart = gen.EmbeddedSignupStart & {
  /**
   * Who Meta bills for the account (5 October 2026): `platform` with the
   * platform's credit line, `customer` for the business's own account. More
   * `reason` values: `NOT_ON_PLAN` (own accounts are Growth and Scale only) and
   * `ACCEPT_META_BILLING` (the business pays and did not agree).
   */
  payer?: MetaBillingPayer | null;
  /** The sentence for `NOT_ON_PLAN` and `ACCEPT_META_BILLING`. */
  message?: string | null;
  /**
   * What to pass to Meta as `extras.featureType` (3 October 2026):
   * `whatsapp_business_app_onboarding` for coexistence, else empty. `reason` is
   * `COEXISTENCE_OFF` when coexistence was asked for and is switched off.
   */
  feature_type?: string | null;
};

/**
 * The steps of the flow, in order, as the service names them.
 *
 * Narrowed by hand: the service stores `current_step` as a plain string column
 * (per the house rule against C# enums for domain values), so the OpenAPI
 * document cannot narrow it. These are the only seven values written.
 */
export type SignupStep =
  | 'launched'
  | 'meta_returned'
  | 'token_exchanged'
  /** The platform's Meta credit line is shared with the customer's account: Meta bills the platform, and the customer needs no payment method (21 September 2026). */
  | 'billing_attached'
  | 'app_subscribed'
  | 'number_registered'
  | 'credentials_stored'
  | 'number_attached'
  /** Coexistence: Meta was asked for the app's contacts and chat history. */
  | 'history_requested';

/** Narrowed by hand for the same reason as {@link SignupStep}. */
export type SignupStatus = 'started' | 'in_progress' | 'completed' | 'failed' | 'cancelled';

/**
 * One connection attempt. Carries no credential: not the account's token, not
 * the six-digit registration PIN.
 *
 * `error_code` is the platform's own vocabulary, which the console translates.
 * `NUMBER_IN_WHATSAPP_APP` is the common one: a number signed in to the
 * WhatsApp Business app cannot also run on the API, so the owner has to choose.
 * `error_message` is Meta's own sentence, kept for support.
 */
export type SignupSession = Omit<gen.SignupSessionDto, 'status' | 'current_step' | 'steps'> & {
  /**
   * Which kind of attempt: Meta's own window, or the guided move out of the
   * WhatsApp Business app. The steps differ per flow; read `steps` rather
   * than assuming the Embedded Signup list.
   */
  flow?: SignupFlow;
  status: SignupStatus;
  current_step: SignupStep | MoveFromAppStep;
  /** Every step in order, so progress renders without hard-coding the list. */
  steps: (SignupStep | MoveFromAppStep)[];
};

/** Narrowed by hand: the service stores it as a checked varchar. */
export type SignupFlow = 'embedded_signup' | 'move_from_app' | 'coexistence';

/**
 * `code` is the short-lived authorisation code from `FB.login()`'s callback;
 * the two ids come off Meta's `WA_EMBEDDED_SIGNUP` FINISH message. All three
 * are required in practice, though the generated type declares them nullable
 * (the service answers 400 for each missing one by name).
 */
export interface CompleteEmbeddedSignupRequest {
  code: string;
  /** Required, except for a coexistence session, where Meta may name only the account and the number is found from it. */
  phone_number_id?: string;
  waba_id: string;
  session_id?: string;
}

/**
 * `error_code` is present for Meta's ERROR event and absent when the person
 * simply closed the window.
 */
export interface CancelEmbeddedSignupRequest {
  session_id?: string;
  error_code?: string;
  error_message?: string;
}

/**
 * Open a connection attempt and get the configuration the browser needs.
 * Check `available` before opening Meta's window.
 */
export interface StartEmbeddedSignupRequest {
  mode?: 'coexistence';
  /**
   * The person agreed that Meta bills their business directly for WhatsApp
   * messages. Required when preflight says `payer: 'customer'`; without it
   * start answers `available: false, reason: 'ACCEPT_META_BILLING'`.
   */
  accept_meta_billing?: boolean;
}

export function startEmbeddedSignup(opts?: StartEmbeddedSignupRequest): Promise<Envelope<EmbeddedSignupStart>> {
  const body: Record<string, unknown> = {};
  if (opts?.mode) body.mode = opts.mode;
  if (opts?.accept_meta_billing) body.accept_meta_billing = true;
  return http.post<Envelope<EmbeddedSignupStart>>('/v1/numbers/embedded-signup/start', { body });
}

/**
 * Hand Meta's answer to the platform, which exchanges the code, subscribes to
 * the account, registers the number and attaches it.
 *
 * Idempotent on the phone number: a repeat call for a number already connected
 * answers with `already_connected: true` rather than registering it twice.
 * Fails with 502 and `{ code, message, step }` naming the step that refused,
 * or 409 `NOT_ON_PLAN` (with `plan_name`) when a business-paid connection's
 * plan no longer includes own accounts: narrow with {@link isOwnAccountNotOnPlan}.
 */
export function completeEmbeddedSignup(
  req: CompleteEmbeddedSignupRequest
): Promise<Envelope<SignupSession>> {
  return http.post<Envelope<SignupSession>>('/v1/numbers/embedded-signup/complete', { body: req });
}

/**
 * Record that Meta's window was closed or reported a problem, so an abandoned
 * connection can be told apart from one that was never started.
 */
export function cancelEmbeddedSignup(
  req: CancelEmbeddedSignupRequest = {}
): Promise<Envelope<SignupSession>> {
  return http.post<Envelope<SignupSession>>('/v1/numbers/embedded-signup/cancel', { body: req });
}

/**
 * Whether this customer can start a connection right now, asked before the
 * browser loads Facebook's SDK.
 *
 * The failure this exists to stop was silent: the SDK loaded against an app
 * with no Embedded Signup configuration id, fell back to a plain login, and
 * Meta answered "This app needs at least one supported permission", which
 * tells the person in front of it nothing at all.
 *
 * `fault` says whose problem it is. `platform` means something only the
 * platform can fix, so the console shows the manual form and one honest
 * sentence rather than sending the customer to a Meta setting they cannot
 * reach; the real reason is logged server-side for support. The customer's own
 * ceiling (the plan's numbers allowance) is refused the usual way instead, with
 * a 409 `PLAN_LIMIT_REACHED`: narrow it with {@link isPlanLimitReached}.
 */
export interface EmbeddedSignupPreflight {
  ready: boolean;
  /**
   * `NOT_SWITCHED_ON`; `NOT_ON_PLAN` (with `fault: 'customer'`, `message` and
   * `plan_name`) when the business would pay Meta and its plan does not include
   * an own account; absent when ready.
   */
  reason?: string;
  /** `platform` or `customer`, absent when ready. */
  fault?: 'platform' | 'customer';
  /** True when the console should offer the manual attach form instead. */
  use_manual_form: boolean;
  checked_at: string;
  /** Whether "Keep using the WhatsApp Business app" (coexistence) may be offered. Off until the platform switches it on. */
  coexistence_available?: boolean;
  /**
   * Who Meta would bill (5 October 2026). `customer`: the console asks the
   * person to agree before starting and sends `accept_meta_billing: true`.
   * Absent when nobody can pay (not switched on).
   */
  payer?: MetaBillingPayer | null;
  /** The sentence for `NOT_ON_PLAN`. */
  message?: string | null;
  /** The plan's name, for `NOT_ON_PLAN`. */
  plan_name?: string | null;
}

/** Ask before opening Meta's window. See {@link EmbeddedSignupPreflight}. */
export function embeddedSignupPreflight(): Promise<Envelope<EmbeddedSignupPreflight>> {
  return http.get<Envelope<EmbeddedSignupPreflight>>('/v1/numbers/embedded-signup/preflight');
}

/** The customer's most recent attempt, or `null` when they have never started one. */
export function getEmbeddedSignupStatus(): Promise<Envelope<SignupSession | null>> {
  return http.get<Envelope<SignupSession | null>>('/v1/numbers/embedded-signup/status');
}

// ── Admin: connection attempts across every customer (W22) ─────────────────
//
// Lives here rather than in `./admin.ts` because it is channel-wa's route and
// reads channel-wa's own types. The admin console's WhatsApp Business Accounts
// page was assembled from the numbers inventory, so it could only show
// connections that had already succeeded: precisely not the ones needing help.

/** One entry in a session's transition log. */
export type SignupTransition = Omit<gen.SignupTransition, 'step' | 'status'> & {
  step: SignupStep;
  status: SignupStatus;
};

/**
 * A connection attempt as the platform console reads it. Wider than
 * {@link SignupSession}: it names the organisation and carries the full,
 * append-only transition log, so a failed attempt that later succeeded keeps
 * both. Still carries no credential.
 */
export type AdminSignupSession = Omit<gen.AdminSignupSessionDto, 'status' | 'current_step' | 'transitions'> & {
  /** `embedded_signup` or `move_from_app`. Absent only from an older service. */
  flow?: SignupFlow;
  status: SignupStatus;
  current_step: SignupStep | MoveFromAppStep;
  transitions: SignupTransition[];
};

export interface AdminSignupSessionListOpts {
  org_id?: string;
  status?: SignupStatus;
  /** Only one kind of attempt. */
  flow?: SignupFlow;
  /** 1-500, default 200. */
  limit?: number;
}

/** Every guided connection attempt, newest first. Platform admin only. */
export function listSignupSessions(
  opts: AdminSignupSessionListOpts = {}
): Promise<Envelope<AdminSignupSession[]> & { meta?: { count?: number } }> {
  const query: Record<string, string | number | undefined> = {};
  if (opts.org_id !== undefined) query.org_id = opts.org_id;
  if (opts.status !== undefined) query.status = opts.status;
  if (opts.flow !== undefined) query.flow = opts.flow;
  if (opts.limit !== undefined) query.limit = opts.limit;
  return http.get<Envelope<AdminSignupSession[]> & { meta?: { count?: number } }>(
    '/v1/admin/signup-sessions',
    { query }
  );
}

// ── Meta refusals, in one shape, and the guided move out of the WhatsApp
//    Business app (7 September 2026) ─────────────────────────────────────────
//
// A number signed in to the WhatsApp Business app on a phone cannot run on
// the Cloud API, and Meta's own wording for it sends people looking in the
// wrong place. There is no API that deletes the app account; only the person
// holding the phone can. So the platform explains why, gives the exact taps,
// notices when the number is free, and finishes the enrolment itself: request
// the code, take the six digits, register with a PIN, attach.
//
// Every path that talks to Meta (attach, Embedded Signup, template submit,
// the move) refuses in the shape below, so one component explains all of
// them. Narrow an error with {@link isMetaRefusal}.

/** The platform's vocabulary for what Meta refused. */
export type MetaRefusalCode =
  | 'NUMBER_IN_WHATSAPP_APP'
  | 'NUMBER_NOT_VERIFIED'
  | 'CODE_LIMIT'
  | 'CODE_MISMATCH'
  | 'NUMBER_CAPACITY'
  | 'NUMBER_ELSEWHERE'
  | 'NO_ACCESS'
  | 'TEMPLATES_NOT_ALLOWED'
  | 'REGISTRATION_PIN_REJECTED'
  | 'NUMBER_NOT_ON_ACCOUNT'
  | 'WINDOW_CLOSED'
  | 'ACCOUNT_NOT_PAYABLE'
  | 'UNDELIVERABLE'
  | 'NUMBER_NOT_REGISTERED'
  | 'MEDIA_NOT_FETCHED'
  | 'META_REFUSED'
  /** A business profile write (15 October 2026): the picture is under 192 × 192. Choose it again through a picture editor that makes 640 × 640. */
  | 'PROFILE_PICTURE_TOO_SMALL'
  /** A business profile write: Meta refused a field and named it. `field` and `problems` say which, with its rule. */
  | 'PROFILE_FIELD_REFUSED'
  /** A business profile write: Meta's rate limit. Wait (until `retry_after` when set), then save again. */
  | 'PROFILE_RATE_LIMITED'
  /** A business profile write: Meta's own temporary trouble. Try again shortly. */
  | 'PROFILE_META_TEMPORARY'
  /** A business profile write Meta refused without saying why: `next_step` lists everything WhatsApp checks, and `request_id` is the reference to quote. */
  | 'PROFILE_REFUSED'
  | (string & {});

/**
 * What the console should offer next. `move_from_app` opens the guided move;
 * `verify` opens it at the code step; `wait` shows `retry_after` as a time;
 * `retry_code` keeps the code box open; `other_number` and `retry` are hints;
 * `fix` means change what was sent (the field in `problems`, or the picture)
 * before trying again; `none` means there is nothing the customer can do and
 * the platform has been told.
 */
export type MetaRefusalAction =
  | 'move_from_app' | 'verify' | 'wait' | 'retry_code' | 'other_number' | 'retry'
  /** Only the customer can do this, and only inside Meta: put a payment method on their WhatsApp Business Account. */
  | 'add_payment_method'
  /** Change what was sent first: sending it unchanged fails the same way. */
  | 'fix'
  | 'none';

/**
 * Meta's refusal in customer language. `message` says what is wrong,
 * `next_step` what to do. `fault` is whose problem it is: a `platform` fault
 * is one the customer cannot fix, so the console says we know and shows
 * nothing about Meta settings they cannot reach; the real reason is logged
 * server-side. Meta's own sentence is never on the wire.
 */
export interface MetaRefusal {
  code: MetaRefusalCode;
  message: string;
  next_step: string;
  action: MetaRefusalAction;
  fault: 'customer' | 'platform';
  /** When Meta limits codes: the time to try again, ISO 8601. */
  retry_after?: string;
  phone_number_id?: string;
  msisdn?: string;
  /** The step that refused, on paths that have steps. */
  step?: string;
  session_id?: string;
  /** The first field the refusal is about, when Meta named one (a business profile write). */
  field?: string;
  /** Every field the refusal is about, with what it must be: the same shape as `PROFILE_INVALID`. */
  problems?: { field: string; message: string }[];
  /** The trace id that finds the refused request on the Logs page; quote it to support. */
  request_id?: string;
}

/** True when `err` carries a Meta refusal, narrowing `err.body.error` to {@link MetaRefusal}. */
export function isMetaRefusal(err: unknown): err is { status: number; body: { error: MetaRefusal } } {
  const e = err as { status?: number; body?: { error?: { code?: unknown; next_step?: unknown; action?: unknown } } } | null;
  return !!e && typeof e.body?.error?.code === 'string' && typeof e.body.error.next_step === 'string' && typeof e.body.error.action === 'string';
}

// ── Delivery failures ──────────────────────────────────────────────────────
//
// A refusal that arrives AFTER Meta accepted the message. The send answered
// HTTP 200 with a wamid, the wallet was charged, and Meta then said on the
// status webhook that the message went nowhere. All that reaches a console is
// Meta's numeric code, on the failed message itself, so the mapping from that
// number to the platform's own vocabulary lives here rather than being
// re-derived in each console.
//
// The same table is `MetaRefusals.ForDelivery` in `services/channel-wa`, and
// the refusal it produces is what the ledger's refund line quotes, so a
// customer reading "not delivered" on a bubble and "not delivered" on their
// ledger is reading one sentence written once.

/** The delivery failures a console has words for. */
export type DeliveryFailureCode =
  | 'WINDOW_CLOSED'
  | 'ACCOUNT_NOT_PAYABLE'
  | 'UNDELIVERABLE'
  | 'NUMBER_NOT_REGISTERED'
  | 'MEDIA_NOT_FETCHED'
  | 'META_REFUSED';

/**
 * What a delivery failure means, from Meta's numeric code on the failed
 * message.
 *
 * `retryable` is deliberately narrow. Sending the same plain text again after
 * `WINDOW_CLOSED` fails identically (it needs an approved template), and after
 * `ACCOUNT_NOT_PAYABLE` every message fails until somebody adds a payment
 * method inside Meta. Only a refusal the platform cannot name is worth
 * offering a retry for, because that is the one that is plausibly transient.
 *
 * `account_wide` marks the failures that are not about this message at all:
 * every send on the number will fail the same way until the customer fixes it,
 * which is why the number carries a standing block for these.
 */
export function deliveryFailure(metaCode: string | number | null | undefined): {
  code: DeliveryFailureCode;
  retryable: boolean;
  account_wide: boolean;
} {
  const n = typeof metaCode === 'number' ? metaCode : Number.parseInt(String(metaCode ?? ''), 10);
  switch (n) {
    // The customer's WhatsApp Business Account has no working payment method.
    case 131042: return { code: 'ACCOUNT_NOT_PAYABLE', retryable: false, account_wide: true };
    // The 24-hour service window closed. 470 is Meta's older code for it.
    case 131047:
    case 470: return { code: 'WINDOW_CLOSED', retryable: false, account_wide: false };
    // WhatsApp would not put it on the handset.
    case 131026: return { code: 'UNDELIVERABLE', retryable: false, account_wide: false };
    // The SENDING number is not registered, so nothing on it is arriving.
    case 131045: return { code: 'NUMBER_NOT_REGISTERED', retryable: false, account_wide: true };
    // Meta could not fetch the file the message pointed at.
    case 131052: return { code: 'MEDIA_NOT_FETCHED', retryable: false, account_wide: false };
    default: return { code: 'META_REFUSED', retryable: true, account_wide: false };
  }
}

/**
 * Why a number cannot send anything at all, on `GET /v1/numbers` and on
 * `GET /v1/messages/summary`. Absent while the number is sending.
 *
 * This is not a per-message failure. Meta accepts every message on a blocked
 * number and refuses all of them afterwards, so without this a console shows a
 * healthy number beside a growing pile of failed messages and never joins the
 * two. `since` is when the block was first seen, not the latest sighting, so a
 * page can say how long it has stood.
 */
export interface SendBlock {
  code: DeliveryFailureCode;
  /** Meta's own numeric code, e.g. "131042", for support to match against Meta's documentation. */
  meta_code?: string | null;
  /** Meta's own wording. For support and the log, not for the page: the console shows its own sentence. */
  detail?: string | null;
  since?: string | null;
}

/** The organisation's worst standing block, as the dashboard summary carries it. */
export interface SendBlockSummary extends SendBlock {
  /** The number it is on, so an alert can link straight to it. */
  phone_number_id: string;
  msisdn?: string | null;
  /** How many of the organisation's numbers are in the same state, so one alert covers them all. */
  numbers_blocked: number;
}

/** The steps of the guided move, in order, as the service names them. */
export type MoveFromAppStep =
  | 'started'
  | 'number_on_account'
  | 'app_deleted'
  | 'code_requested'
  | 'number_verified'
  | 'number_registered'
  | 'number_attached';

/** The number as Meta holds it right now. */
export interface MoveNumber {
  phone_number_id: string;
  msisdn?: string;
  display_name?: string;
  /** Meta's `is_on_biz_app`: still signed in to the WhatsApp Business app on a phone. */
  on_app: boolean;
  verified: boolean;
  /** False when Meta omitted the verification field, which is not the same as unverified. */
  verification_known: boolean;
  status?: string;
  state: 'on_business_app' | 'unverified' | 'limited' | 'not_registered' | 'connected';
  checked_at: string;
}

/** Which step the console should show. Derived by the service, never stored. */
export type MoveFromAppNext = 'start' | 'check' | 'delete_app' | 'verify' | 'connect' | 'done' | 'cancelled';

/**
 * One guided move. Carries no credential. `number` is Meta's fresh answer
 * about the number when there is one; `check_detail` is the sentence for the
 * check step when the move is waiting (Meta can lag a few minutes after the
 * app account is deleted).
 */
export interface MoveFromAppSession {
  id: string;
  flow: 'move_from_app';
  status: SignupStatus;
  current_step: MoveFromAppStep;
  steps: MoveFromAppStep[];
  waba_id?: string;
  phone_number_id?: string;
  display_name?: string;
  msisdn?: string;
  meta_status?: string;
  error_code?: string;
  error_message?: string;
  phone_number_row_id?: string;
  already_connected: boolean;
  number?: MoveNumber;
  next: MoveFromAppNext;
  check_detail?: string;
  /** `sms` or `voice`, on the answer to a code request. */
  code_method?: 'sms' | 'voice';
  started_at: string;
  completed_at?: string;
  updated_at: string;
}

/**
 * One of `msisdn` (the phone number with its country code) or
 * `phone_number_id` (Meta's id, when a refusal already named it).
 * `display_name` is the name WhatsApp shows and is only needed when the
 * number has to be added to the account and the organisation has no name on
 * file (the service answers 400 `NAME_REQUIRED`).
 */
export interface StartMoveFromAppRequest {
  msisdn?: string;
  phone_number_id?: string;
  display_name?: string;
}

/**
 * Start moving a number out of the WhatsApp Business app.
 *
 * Enforces the plan's numbers allowance first (409 `PLAN_LIMIT_REACHED`,
 * narrow with {@link isPlanLimitReached}), then finds the number on the
 * WhatsApp Business Account the platform manages, adding it when it is not
 * there yet. Read `next` to know which step to show: `delete_app` while the
 * number is still on the app, `verify` once it is free, `connect` once
 * verified.
 */
export function startMoveFromApp(req: StartMoveFromAppRequest): Promise<Envelope<MoveFromAppSession>> {
  return http.post<Envelope<MoveFromAppSession>>('/v1/numbers/move-from-app/start', { body: req });
}

/**
 * The session plus a fresh read of the number from Meta. The "I have deleted
 * it" button is this call: while `number.on_app` stays true, `check_detail`
 * says Meta has not noticed yet and to try again shortly.
 */
export function getMoveFromApp(sessionId: string): Promise<Envelope<MoveFromAppSession>> {
  return http.get<Envelope<MoveFromAppSession>>(`/v1/numbers/move-from-app/${encodeURIComponent(sessionId)}`);
}

/**
 * Ask WhatsApp for the verification code by SMS or voice call. When Meta
 * limits codes the refusal is `CODE_LIMIT` with `retry_after` set about an
 * hour ahead; show it as a time.
 */
export function requestMoveCode(
  sessionId: string,
  req: { method: 'sms' | 'voice'; language?: string }
): Promise<Envelope<MoveFromAppSession>> {
  return http.post<Envelope<MoveFromAppSession>>(`/v1/numbers/move-from-app/${encodeURIComponent(sessionId)}/request-code`, { body: req });
}

/** Give WhatsApp the six digits. A wrong or stale code is `CODE_MISMATCH`, and the box stays open. */
export function verifyMoveCode(sessionId: string, code: string): Promise<Envelope<MoveFromAppSession>> {
  return http.post<Envelope<MoveFromAppSession>>(`/v1/numbers/move-from-app/${encodeURIComponent(sessionId)}/verify`, { body: { code } });
}

/**
 * Register the number for sending with a PIN the platform generates and
 * keeps, make sure incoming messages are switched on, and attach it.
 * Idempotent: a second call answers `already_connected: true` and makes no
 * call to Meta.
 */
export function registerMoveFromApp(sessionId: string): Promise<Envelope<MoveFromAppSession>> {
  return http.post<Envelope<MoveFromAppSession>>(`/v1/numbers/move-from-app/${encodeURIComponent(sessionId)}/register`, { body: {} });
}

/** Stop a move. Nothing is changed on the phone. */
export function cancelMoveFromApp(sessionId: string): Promise<Envelope<MoveFromAppSession>> {
  return http.post<Envelope<MoveFromAppSession>>(`/v1/numbers/move-from-app/${encodeURIComponent(sessionId)}/cancel`, { body: {} });
}

// ── The template library (8 September 2026) ────────────────────────────────
//
// A brand new WhatsApp Business Account has no templates, so the first thing a
// customer met on the Templates page was an empty list and Meta's component
// schema. Two sources fill it and they are NOT the same claim:
//
//   `meta`    Meta's own pre-written, pre-reviewed contents, read from
//             `message_template_library` on the Graph API. Adopted by naming
//             the entry (`library_template_name`), which is normally the
//             faster path through review because the wording is Meta's.
//   `starter` Wording written by the platform, seeded by
//             `databases/channel-wa/migrations/20260908_010_TemplateLibrary.sql`.
//             Submitted the ordinary way, with its components.
//
// Meta's library is UTILITY-only, written for e-commerce and financial
// services, and not open to every app or account, so the starters sit beside
// it rather than instead of it. `meta.meta_available` says whether Meta
// answered; a console must never present a starter as Meta's.
//
// Hand-typed against the service's `.Produces<T>()` records until the
// generator is next run.

/** One entry on the library screen, from either source. */
export interface TemplateLibraryEntry {
  /** `meta:name` or `starter:name`. What `adoptLibraryTemplate` takes. */
  key: string;
  source: 'meta' | 'starter';
  name: string;
  language: string;
  category: string;
  /** Meta's classification, e.g. `ORDER_MANAGEMENT`. */
  topic?: string | null;
  /** Meta's use case, e.g. `DELIVERY_UPDATE`. */
  usecase?: string | null;
  industry: string[];
  /** What a person reads on the card. */
  title: string;
  /** One plain sentence on when to send it. Starters carry this; Meta's do not. */
  purpose?: string | null;
  /** TEXT, IMAGE, VIDEO, DOCUMENT or NONE. */
  header_format: string;
  header?: string | null;
  body: string;
  footer?: string | null;
  /** Positional sample values, one per placeholder. */
  body_params: string[];
  buttons: Array<{ type?: string; text?: string; url?: string; phone_number?: string }>;
  /**
   * The entry as Meta's `components` array, examples included: ready to
   * pre-fill an editor and to post straight back to `/v1/templates`. A string,
   * exactly as a template's own `components` is.
   */
  components: string;
}

export interface TemplateLibraryOpts {
  search?: string;
  /** utility | marketing | authentication. Applied to both sources. */
  category?: string;
  /** Meta's `topic`, e.g. `PAYMENTS`. */
  topic?: string;
  /** Meta's `usecase`, e.g. `DELIVERY_UPDATE`. */
  usecase?: string;
  /** Meta's `industry`, e.g. `E_COMMERCE`. */
  industry?: string;
  language?: string;
}

export interface TemplateLibraryMeta {
  count: number;
  /**
   * Whether Meta's own library edge served this account. False is ordinary,
   * not a fault: say so on screen rather than showing a short list.
   */
  meta_available: boolean;
  meta_count: number;
  starter_count: number;
}

/** Browse the library. */
export function templateLibrary(
  opts: TemplateLibraryOpts = {}
): Promise<Envelope<TemplateLibraryEntry[]> & { meta: TemplateLibraryMeta }> {
  const query: Record<string, string | undefined> = {};
  if (opts.search !== undefined) query.search = opts.search;
  if (opts.category !== undefined) query.category = opts.category;
  if (opts.topic !== undefined) query.topic = opts.topic;
  if (opts.usecase !== undefined) query.usecase = opts.usecase;
  if (opts.industry !== undefined) query.industry = opts.industry;
  if (opts.language !== undefined) query.language = opts.language;
  return http.get<Envelope<TemplateLibraryEntry[]> & { meta: TemplateLibraryMeta }>(
    '/v1/templates/library',
    { query }
  );
}

/** One entry, by `meta:name` or `starter:name`. A bare name looks in both. */
export function templateLibraryEntry(key: string): Promise<Envelope<TemplateLibraryEntry>> {
  return http.get<Envelope<TemplateLibraryEntry>>(`/v1/templates/library/${encodeURIComponent(key)}`);
}

export interface AdoptLibraryTemplateRequest {
  /** The entry's `key`. */
  key: string;
  /** What to call it here. Defaults to the entry's own name. */
  name?: string;
  language?: string;
  category?: string;
  /** Send it to Meta now. Defaults to true; false leaves a draft. */
  submit?: boolean;
  /** Meta's `library_template_button_inputs`, passed straight through. */
  button_inputs?: unknown;
  /** Meta's `library_template_body_inputs`, passed straight through. */
  body_inputs?: unknown;
}

/**
 * Take a library entry into this account.
 *
 * The local row is written from the entry's components either way, so the
 * template renders in the console and in the broadcast composer whether or not
 * Meta has finished reviewing it. A Meta entry is created at Meta by name; a
 * starter is submitted with its components. Both end in exactly the state an
 * ordinary submit ends in: same status, same `meta_template_id`, same
 * `template.submitted` event, same webhook and poller behaviour afterwards.
 */
export function adoptLibraryTemplate(
  req: AdoptLibraryTemplateRequest
): Promise<Envelope<import('./templates.js').MessageTemplate>> {
  return http.post<Envelope<import('./templates.js').MessageTemplate>>(
    '/v1/templates/library/adopt',
    { body: req }
  );
}

// ── The WhatsApp business profile ──────────────────────────────────────────
//
// What a recipient sees when they receive a message from one of the
// customer's numbers and tap it: a picture, a short status line, a
// description, an address, an email, up to two websites and a category. It
// lives on Meta's `whatsapp_business_profile` edge on the PHONE NUMBER, not on
// the WhatsApp Business Account, so it is per number: each customer's number
// is their own business.
//
// Everything on it is public to anybody who receives a message from the
// number. Any interface built on these calls should say so.
//
// The picture is the CUSTOMER's logo. There is deliberately no platform
// default: putting one business's mark on another's profile would misrepresent
// them to their own customers.

/** One number's profile, as Meta holds it. Absent fields are simply unset. */
export interface BusinessProfile {
  /** The short status line beneath the picture. A different field from `description`. */
  about?: string | null;
  address?: string | null;
  /** The paragraph further down the profile. */
  description?: string | null;
  email?: string | null;
  /** Meta's category code, from {@link BusinessProfileMeta.verticals}. */
  vertical?: string | null;
  /** At most two. Always an array, never absent. */
  websites: string[];
  /** Read-only. A write takes a picture reference instead; Meta answers with a URL. */
  profile_picture_url?: string | null;
}

/**
 * Meta's own character limits, served with the read so nothing duplicates them.
 * `websites` is the count Meta accepts; `website` is the length of each one.
 */
export interface BusinessProfileLimits {
  about: number;
  address: number;
  description: number;
  email: number;
  website: number;
  websites: number;
  picture_bytes: number;
  /** The smallest picture Meta takes, on each side (192). Absent from servers before 15 October 2026. */
  picture_min_edge?: number;
  /** The square size Meta recommends and the console's picture editor makes (640). Absent from servers before 15 October 2026. */
  picture_edge?: number;
}

export interface BusinessProfileMeta {
  number: {
    id: string;
    msisdn?: string | null;
    display_name?: string | null;
    meta_phone_number_id: string;
  };
  /**
   * False only when Meta was actually asked on this call. Meta rate-limits
   * profile reads, so the service holds each number's profile briefly; pass
   * `refresh` to go past it.
   */
  cached: boolean;
  read_at: string;
  limits: BusinessProfileLimits;
  /** Meta's fixed category list. Not free text, and not ours to extend. */
  verticals: string[];
}

/**
 * Read one number's business profile.
 *
 * Cached per number for a few minutes because Meta rate-limits profile reads;
 * `refresh: true` asks Meta again, and `meta.cached` says which happened so an
 * interface never claims a stale read is live.
 */
export function businessProfile(
  numberId: string,
  opts: { refresh?: boolean } = {}
): Promise<Envelope<BusinessProfile> & { meta: BusinessProfileMeta }> {
  const query: Record<string, string | undefined> = {};
  if (opts.refresh) query.refresh = 'true';
  return http.get<Envelope<BusinessProfile> & { meta: BusinessProfileMeta }>(
    `/v1/numbers/${encodeURIComponent(numberId)}/business-profile`,
    { query }
  );
}

/**
 * What to change. Every field is optional and an absent one is left alone, so
 * editing the status line cannot wipe the address. Send an empty string to
 * clear a field, or an empty `websites` array to clear the websites.
 */
export interface UpdateBusinessProfileRequest {
  /** At most 139 characters. Markdown is not rendered and links are not clickable. */
  about?: string;
  /** At most 256 characters. */
  address?: string;
  /** At most 512 characters. */
  description?: string;
  /** At most 128 characters, and it has to look like an email address. */
  email?: string;
  /** One of `meta.verticals`. Anything else is refused with `PROFILE_INVALID`. */
  vertical?: string;
  /** At most two, each with its `http://` or `https://` and at most 256 characters. */
  websites?: string[];
  /**
   * The customer's logo: a media id from `media.upload()`, or a public
   * http(s) URL. The service fetches the bytes and pushes them to Meta's
   * resumable upload API, then writes the handle Meta gives back. Meta
   * refuses anything under 192 × 192; send a square JPG, ideally 640 × 640
   * (`ImageCropper` in `@repletoryreach/sdk-ui/image-cropper` makes one).
   */
  profile_picture_media_id?: string;
}

/**
 * Change the profile and get back what it now says.
 *
 * Meta's limits are checked before anything is sent, so a bad field comes back
 * as 400 `PROFILE_INVALID` naming the field and the count rather than as
 * Graph's "(#100) Invalid parameter". The response is a fresh read, not an
 * echo: Meta normalises some values, and the picture only ever comes back as a
 * URL.
 *
 * What Meta itself refuses comes back as a {@link MetaRefusal} (narrow with
 * {@link isMetaRefusal}): 400 `PROFILE_PICTURE_TOO_SMALL`,
 * `PROFILE_FIELD_REFUSED` (with `field` and `problems`) or `PROFILE_REFUSED`
 * (action `fix`); 409 `PROFILE_RATE_LIMITED` (action `wait`); 502
 * `PROFILE_META_TEMPORARY` (action `retry`), or `NO_ACCESS` when the fault is
 * the platform's. Only `PROFILE_META_TEMPORARY` is worth sending again
 * unchanged, and `PROFILE_RATE_LIMITED` once the wait is over.
 */
export function updateBusinessProfile(
  numberId: string,
  req: UpdateBusinessProfileRequest
): Promise<Envelope<BusinessProfile>> {
  return http.post<Envelope<BusinessProfile>>(
    `/v1/numbers/${encodeURIComponent(numberId)}/business-profile`,
    { body: req }
  );
}
